import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { cn } from "@/lib/cn";
import { useUiStore } from "@/store/uiStore";

interface Props {
  onClick: () => void;
  /**
   * The list's fetch flag. Only used to decide when a refresh *this button
   * started* has finished — it never spins the icon on its own, since it is
   * also true for page changes, sorting and filtering.
   */
  loading?: boolean;
  className?: string;
  title?: string;
}

/**
 * Icon-only reload control for list pages. Sits in the page header next to the
 * primary action and refetches the current page — page number, sort and
 * filters are left untouched.
 */
export function RefreshButton({ onClick, loading = false, className, title = "Refresh" }: Props) {
  const [spinning, setSpinning] = useState(false);
  const startPageLoading = useUiStore((s) => s.startPageLoading);
  const stopPageLoading = useUiStore((s) => s.stopPageLoading);

  useEffect(() => {
    // Every refresh drives the global PageLoader, whether or not the host page
    // wires list.loading into it.
    if (!spinning) return;
    startPageLoading();
    return () => stopPageLoading();
  }, [spinning, startPageLoading, stopPageLoading]);

  useEffect(() => {
    if (!spinning || loading) return;
    // The fetch has settled (or the caller never reported one). Keep the spin a
    // beat longer so a fast response still reads as a deliberate refresh.
    const timer = setTimeout(() => setSpinning(false), 400);
    return () => clearTimeout(timer);
  }, [spinning, loading]);

  return (
    <button
      type="button"
      onClick={() => {
        setSpinning(true);
        onClick();
      }}
      disabled={spinning}
      title={title}
      aria-label={title}
      className={cn(
        "inline-flex h-8 w-8 items-center justify-center rounded-sm border border-bridge-border/70",
        "bg-bridge-surface/80 text-bridge-muted shadow-soft transition-all hover:bg-bridge-panel hover:text-bridge-fg",
        "disabled:opacity-50 disabled:cursor-not-allowed focus-ring",
        className,
      )}
    >
      <RefreshCw size={15} className={cn(spinning && "animate-spin")} />
    </button>
  );
}
